import React from 'react';
import styled from 'styled-components';

import {
  FormGroup,
  FormHelpText,
  FormInput as EncoreFormInput,
  FormSelect,
  Type,
} from '@spotify-internal/encore-creator-web';
import { gray10 } from '@spotify-internal/encore-foundation';

import {
  faqUrl,
  musicReliefOrgDisplayNamesWithCountries,
  musicReliefOrgLinks,
} from '../../lib/constants';
import { unreachable } from '../../lib/unreachable';
import { setPrefix } from '../../lib/validations';
import type { DialogBodyProps, Org, Provider } from '../../types';
import { TextLinkUnderlined } from '../StyleElements';

const FormHelpTextFontSize14 = styled(FormHelpText)`
  font-size: 14px;
  line-height: 20px;
`;

const LabelSpan16 = styled(Type).attrs({
  color: gray10,
  variant: Type.body2,
})`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${Type.bold};
`;

const FormInputPreventAutoZoomIOS = styled(EncoreFormInput)`
  /* font-size of at least 16px is required in order to prevent triggering
    awkward auto-zoom accessibility feature in iOS */
  font-size: 16px;
`;

const FormSelectPreventAutoZoomIOS = styled(FormSelect)`
  /* font-size of at least 16px is required in order to prevent triggering
    awkward auto-zoom accessibility feature in iOS */
  font-size: 16px;
`;

const FormGroupPositionRelative = styled(FormGroup)`
  position: relative;
`;

const options: {
  [key in Provider]: {
    disclaimer?: React.ReactElement;
    displayName: string;
    type: 'text' | 'select';
  };
} = {
  cashapp: {
    type: 'text',
    displayName: 'Cash App',
  },
  givealittle: {
    type: 'text',
    disclaimer: (
      <>
        Only choose Givealittle as your payment provider if you're based in New
        Zealand.
      </>
    ),
    displayName: 'Givealittle',
  },
  gofundme: {
    type: 'text',
    displayName: 'GoFundMe',
  },
  payu_turkey: {
    type: 'text',
    disclaimer: (
      <>
        Only choose iyzico, a PayU company (Turkey) as your payment provider if
        you're based in Turkey. You can only receive donations from people in
        Turkey.
      </>
    ),
    displayName: 'iyzico, a PayU company (Turkey)',
  },
  mercadopago: {
    type: 'text',
    disclaimer: (
      <>
        Only choose Mercado Pago as your payment provider if you're based in
        Argentina, Brazil, Chile, Colombia, Mexico, Peru, or Uruguay. Mercado
        Pago will ask you to set a fixed donation amount.
      </>
    ),
    displayName: 'Mercado Pago',
  },
  musicrelieforg: {
    type: 'select',
    displayName: 'Choose Music Relief organization',
  },
  paypal: {
    type: 'text',
    disclaimer: (
      <>
        Don’t choose PayPal.me as your payment provider if you're based in one
        of these countries: Argentina, Bahrain, Brazil, Chile, Colombia, Costa
        Rica, Dominican Republic, Ecuador, El Salvador, Guatemala, Honduras,
        Hong Kong, India, Israel, Jordan, Malaysia, Mexico, Nicaragua, Oman,
        Panama, Peru, Qatar, Saudi Arabia, Singapore, United Arab Emirates, or
        Uruguay.{' '}
        <TextLinkUnderlined href={faqUrl} target="faq">
          Learn more
        </TextLinkUnderlined>
      </>
    ),
    displayName: 'PayPal.me',
  },
  payu_latam: {
    type: 'text',
    disclaimer: (
      <>
        Only choose PayU (LATAM) as your payment provider if you're based in one
        of these countries: Argentina, Brazil, Chile, Colombia, Mexico, or Peru.
        PayU will ask you to set a fixed donation amount.
      </>
    ),
    displayName: 'PayU (LATAM)',
  },
};

export function InputGroup(
  props: DialogBodyProps & {
    isFundraisingLinkSelected: boolean;
    setIsFundraisingLinkSelected: (isSelected: boolean) => void;
  },
) {
  return (
    <>
      {props.isFundraisingLinkSelected ? (
        <ProviderSelectGroup {...props} />
      ) : null}
      <LinkInputGroup {...props} />
    </>
  );
}

function ProviderSelectGroup(props: DialogBodyProps) {
  const hasErrors = props.provider.errors.length > 0;
  const providerValue = props.provider.value;

  let ariaDescribedBy = hasErrors
    ? 'fundraising-pick-select-input-error-0'
    : undefined;

  let disclaimer = null;
  let disclaimerId = '';
  if (providerValue) {
    disclaimer = options[providerValue]?.disclaimer;
    if (disclaimer && !hasErrors) {
      disclaimerId = `${providerValue}-disclaimer`;
      ariaDescribedBy = disclaimerId;
    }
  }

  function onProviderChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const nextProvider = ev.target.value as Provider | '';
    props.link.reset();
    props.org.reset();
    props.provider.setValue(ev);
    // Make sure not to try
    if (nextProvider) {
      setPrefix(props.link, nextProvider);
    }
  }

  const optionsWithoutMusicReliefOrg = {
    ...options,
    musicrelieforg: undefined,
  };

  return (
    <FormGroup
      label={<LabelSpan16>Choose payment provider</LabelSpan16>}
      labelFor="fundraising-pick-select-input"
    >
      <FormSelectPreventAutoZoomIOS
        id="fundraising-pick-select-input"
        onChange={onProviderChange}
        value={props.provider.value ?? ''}
        error={hasErrors}
        aria-invalid={hasErrors}
        aria-describedby={ariaDescribedBy}
      >
        <option value="">Please select</option>
        {Object.entries(optionsWithoutMusicReliefOrg).map(([key, option]) => {
          if (option) {
            return (
              <option key={key} value={key}>
                {option.displayName}
              </option>
            );
          }
          return null;
        })}
      </FormSelectPreventAutoZoomIOS>
      {/* Hide disclaimer if there are errors */}
      {!hasErrors && !!disclaimer && (
        <FormHelpTextFontSize14 id={disclaimerId}>
          <Type as="p" variant={Type.body2}>
            {disclaimer}
          </Type>
        </FormHelpTextFontSize14>
      )}
      {props.provider.errors?.map((e, i) => (
        <FormHelpTextFontSize14
          id={`fundraising-pick-select-input-error-${i}`}
          key={i}
          error
        >
          {e}
        </FormHelpTextFontSize14>
      )) ?? null}
    </FormGroup>
  );
}

function LinkInputGroup(props: DialogBodyProps) {
  const [touched, setTouched] = React.useState(false);

  const textInputRef = React.useRef<HTMLInputElement>(null);

  // @ts-ignore
  const type: 'text' | 'select' = options[props.provider.value]?.type ?? 'text';

  function onOrgChange(ev: React.ChangeEvent<HTMLInputElement>) {
    if (ev.target.value) {
      props.org.setValue(ev);
      props.link.setInnerValue(musicReliefOrgLinks[ev.target.value as Org]);
    }
  }

  function onPasteTextInput(evt: React.ClipboardEvent) {
    const pastedText = (
      evt.clipboardData || (window as any).clipboardData
    ).getData('text/plain');

    props.link.setValue({ target: { value: pastedText } } as any);

    // Important! We don't want the onChange handler to handle this again
    evt.preventDefault();
  }

  const shouldShowPlaceholder =
    !touched && props.link.value === props.link.initialValue;

  switch (type) {
    case 'text': {
      const hasErrors = props.link.errors.length > 0;

      return (
        <FormGroupPositionRelative
          label={<LabelSpan16>Add your fundraising link</LabelSpan16>}
          labelFor="fundraising-pick-text-input"
        >
          <FormInputPreventAutoZoomIOS
            id="fundraising-pick-text-input"
            type="text"
            ref={textInputRef}
            onChange={props.link.setValue}
            onFocus={() => setTouched(true)}
            value={shouldShowPlaceholder ? '' : props.link.value}
            error={hasErrors}
            aria-invalid={hasErrors}
            onPaste={onPasteTextInput}
            aria-describedby={
              hasErrors ? 'fundraising-pick-text-input-error-0' : undefined
            }
            placeholder={props.link.value || 'Your link'}
          />

          {props.link.errors?.map((e, i) => (
            <FormHelpTextFontSize14
              id={`fundraising-pick-text-input-error-${i}`}
              key={i}
              error
            >
              {e}
            </FormHelpTextFontSize14>
          )) ?? null}
        </FormGroupPositionRelative>
      );
    }
    case 'select': {
      const hasErrors = props.link.errors.length > 0;

      return (
        <FormGroup
          label={<LabelSpan16>Choose Music Relief organization</LabelSpan16>}
          labelFor="fundraising-pick-select-input"
        >
          <FormSelectPreventAutoZoomIOS
            id="fundraising-pick-select-input"
            onChange={onOrgChange}
            value={props.org.value ?? ''}
            error={hasErrors}
            aria-invalid={hasErrors}
            aria-describedby={
              hasErrors ? 'fundraising-pick-select-input-error-0' : undefined
            }
          >
            <option value="">Please select</option>
            {Object.keys(musicReliefOrgDisplayNamesWithCountries).map(
              orgName => {
                const orgDisplayName =
                  // @ts-ignore
                  musicReliefOrgDisplayNamesWithCountries[orgName];
                return (
                  <option key={orgName} value={orgName}>
                    {orgDisplayName}
                  </option>
                );
              },
            )}
          </FormSelectPreventAutoZoomIOS>
          {props.link.errors?.map((e, i) => (
            <FormHelpTextFontSize14
              id={`fundraising-pick-select-input-error-${i}`}
              key={i}
              error
            >
              {e}
            </FormHelpTextFontSize14>
          )) ?? null}
        </FormGroup>
      );
    }
    default:
      unreachable(type);
      return null;
  }
}
